import { FC, useState } from "react";
import { PlusCircle, ArrowRight } from "@phosphor-icons/react";
import CustomButton from "../../../shared/CustomButton/CustomButton";
import Typhography from "../../../shared/Typhography/Typhography";
import { EmptyContainer } from "../../../shared/EmptyContainer/EmptyContainer";
import styles from "./SubstitutionsTab.module.scss";

interface Substitution {
  minute?: number;
  matchHalf?: number;
  playerIn?: string;
  playerOut?: string;
  reason?: string;
  notes?: string;
  playerInName?: string;
  playerOutName?: string;
}

interface SubstitutionsTabProps {
  substitutions: Substitution[];
  onAddSubstitution: (sub: {
    minute?: number;
    playerIn: string;
    playerOut: string;
    reason?: string;
  }) => Promise<void>;
  availablePlayers: Array<{ _id: string; firstName: string; lastName: string }>;
  isLoading?: boolean;
}

const REASONS = [
  { value: "tactical", label: "Tattico" },
  { value: "injury", label: "Infortunio" },
  { value: "fatigue", label: "Fatica" },
  { value: "card", label: "Cartellino" },
  { value: "planned", label: "Programmato" },
  { value: "other", label: "Altro" },
];

/**
 * Tab CAMBI — lista sostituzioni + form aggiunta.
 * Spec: LEOPART_ProgrammazioneCampo_Spec_v1.1.1.md righe 714-740.
 */
const SubstitutionsTab: FC<SubstitutionsTabProps> = ({
  substitutions,
  onAddSubstitution,
  availablePlayers,
  isLoading = false,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    minute: "",
    playerOut: "",
    playerIn: "",
    reason: "tactical",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.playerIn || !form.playerOut) return;
    setSaving(true);
    try {
      await onAddSubstitution({
        minute: form.minute ? parseInt(form.minute, 10) : undefined,
        playerIn: form.playerIn,
        playerOut: form.playerOut,
        reason: form.reason,
      });
      setForm({ minute: "", playerOut: "", playerIn: "", reason: "tactical" });
      setShowForm(false);
    } finally {
      setSaving(false);
    }
  };

  const getReasonLabel = (reason?: string) =>
    REASONS.find((r) => r.value === reason)?.label ?? reason ?? "—";

  if (!substitutions.length && !showForm) {
    return (
      <div className={styles.emptyWrap}>
        <EmptyContainer
          Icon={ArrowRight}
          title="Nessun cambio registrato"
          description="Aggiungi i cambi effettuati durante la partita"
          buttonText="Aggiungi cambio"
          onClick={() => setShowForm(true)}
        />
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.listHeader}>
        <Typhography size="bold-14">Cambi effettuati</Typhography>
        <CustomButton
          size="sm"
          color="secondary"
          onClick={() => setShowForm(true)}
        >
          <PlusCircle size={14} />
          Aggiungi
        </CustomButton>
      </div>

      <div className={styles.list}>
        {substitutions.map((sub, idx) => (
          <div key={idx} className={styles.subRow}>
            <div className={styles.subMinute}>
              <span className={styles.minuteBadge}>
                {sub.minute !== undefined ? `${sub.minute}'` : "—"}
              </span>
              {sub.matchHalf && (
                <span className={styles.halfLabel}>
                  {sub.matchHalf === 2 ? "2T" : "1T"}
                </span>
              )}
            </div>
            <div className={styles.subDetail}>
              <div className={styles.playerChange}>
                <span className={styles.playerIn}>
                  IN: {sub.playerInName ?? sub.playerIn ?? "—"}
                </span>
                <span className={styles.playerOut}>
                  OUT: {sub.playerOutName ?? sub.playerOut ?? "—"}
                </span>
              </div>
              <span className={styles.reason}>{getReasonLabel(sub.reason)}</span>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className={styles.formWrap}>
          <Typhography size="bold-14" className="mb-3">
            Nuovo cambio
          </Typhography>
          <div className={styles.formGrid}>
            <div className={styles.formField}>
              <label className={styles.label}>Minuto</label>
              <input
                type="number"
                className={styles.input}
                placeholder="es. 62"
                value={form.minute}
                onChange={(e) => setForm((f) => ({ ...f, minute: e.target.value }))}
                min={1}
                max={120}
              />
            </div>
            <div className={styles.formField}>
              <label className={styles.label}>Giocatore OUT *</label>
              <select
                className={styles.input}
                value={form.playerOut}
                onChange={(e) => setForm((f) => ({ ...f, playerOut: e.target.value }))}
              >
                <option value="">Seleziona...</option>
                {availablePlayers.map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.firstName} {p.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.formField}>
              <label className={styles.label}>Giocatore IN *</label>
              <select
                className={styles.input}
                value={form.playerIn}
                onChange={(e) => setForm((f) => ({ ...f, playerIn: e.target.value }))}
              >
                <option value="">Seleziona...</option>
                {availablePlayers.map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.firstName} {p.lastName}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.formField}>
              <label className={styles.label}>Motivo</label>
              <select
                className={styles.input}
                value={form.reason}
                onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
              >
                {REASONS.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className={styles.formActions}>
            <CustomButton
              size="sm"
              color="outline"
              onClick={() => setShowForm(false)}
            >
              Annulla
            </CustomButton>
            <CustomButton
              size="sm"
              color="primary"
              onClick={handleSubmit}
              disabled={!form.playerIn || !form.playerOut || saving || isLoading}
            >
              {saving ? "Salvataggio..." : "Salva cambio"}
            </CustomButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubstitutionsTab;
